import {
  amber,
  blue,
  brown,
  cyan,
  deepPurple,
  green,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow
} from "@material-ui/core/colors";
import {
  EnumField,
  NumberField,
  Pattern,
  RangeInc,
  RemoteDataSource,
  Required,
  Schema,
  TextField,
  Type,
  Validator
} from "lego-gui-lib";
import { createContext, ReactText } from "react";
import {
  ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_LAST_SIP_RESPONSE_CODE,
  ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_NUMBER,
  ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_SAN_EXTENSION,
  ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_XRN,
  DigitsEnum,
  NUMERIC_RANGE_PATTERN,
  TIME_RANGE_PATTERN
} from "../../../common/common";
import { t } from "../../../core/i18n/i18n";
import { api, mainStore } from "../../../core/mainStore";
import { Auth } from "../../../core/model/auth";
import { AreaSplittingGroupForTreeFK } from "../../areaSplitting/groups/schema";
import { ListFKCustom } from "../../list/schema";
import {
  CustomerMessagesAnnouncementFK,
  CustomerMessagesFK,
  NumericCodeCustomerMessageFK
} from "../../message/schema";
import { NetworkOperatorFK } from "../../networkOperator/schema";
import {
  outputHasType,
  isOutputFormOfType,
  NumericRangeFromLessIncTo,
  NumericRangeFromSameLengthAsTo,
  sanHasCalendar
} from "../../serviceProfile/validator";
import {
  BlockObject,
  TreeBlockOutputsDataSource,
  OutputType
} from "./treeBlockOutputsDataSource";
import {
  AbstractTreeBlockSchema,
  AdvancedConditionParamValue
} from "./schema/AbstractTreeBlockSchema";
import {
  NumericRangeValidator,
  TimeOfDayValidator,
  TrafficDistributionValidator
} from "./schema/validators";
import { TreeBlockDataSource } from "./TreeBlockDataSource";
import { TreeBlockServiceProfile, TreeBlockType } from "./TreeBlockType";
import { CalendarDayTypesFK } from "../../calendar/calendarView/schema";

export interface LocalOutput {
  id?: any;
  optionId?: ReactText;
  value: any;
  label: (() => string) | string;
  type: OutputType;
  isAddedByDefault?: boolean;
  isDynamicOption?: boolean;
}

export const DestinationTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    destinationRef: {}
  },
  { onlyGiven: true }
);

export const EnRouteDestinationTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    destinationRef: {}
  },
  { onlyGiven: true }
);

export const MessageTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    messageRef: { type: CustomerMessagesFK }
  },
  { onlyGiven: true }
);

export const EnRouteMessageTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    messageRef: {}
  },
  { onlyGiven: true }
);

export const ReleaseCallTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    releaseReason: {}
  },
  { onlyGiven: true }
);
export const ReleaseCallPROTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    releaseResponseCode: { type: NumberField(Required, RangeInc(400, 699)) }
  },
  { onlyGiven: true }
);
export const StandardIVRTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    messageToPlay: { type: CustomerMessagesAnnouncementFK },
    repeatMessage: {},
    repetitionMessageDigit: {},
    maxRepetitions: {},
    timeoutRetries: { type: NumberField(Required, RangeInc(0, 9)) },
    timeoutMessage: {},
    errorRetries: { type: NumberField(Required, RangeInc(0, 9)) },
    errorMessage: {}
  },
  { onlyGiven: true }
);

export const SubTreeTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    subtreeRef: {}
  },
  { onlyGiven: true }
);

export const IVRMenuTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    ivrMenuRef: {}
  },
  { onlyGiven: true }
);

export const NoParametersTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {}
  },
  { onlyGiven: true }
);

export const HuntingGroupTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    huntingGroupRef: {}
  },
  { onlyGiven: true }
);

export const RegistrationCounterTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    counterName: {},
    counterValue: {}
  },
  { onlyGiven: true }
);

export const AdvancedConditionsTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    advancedConditionParam: {}
  },
  { onlyGiven: true }
);

export const ExtAppDestination = AbstractTreeBlockSchema.extend(
  {
    type: {},
    extAppReqType: {},
    extAppUrl: {},
    extAppParams: {},
    extAppRespXPath: {}
  },
  { onlyGiven: true }
);

export const NumericCodeTreeBlockSchema = AbstractTreeBlockSchema.extend(
  {
    type: {},
    messageToPlay: {},
    minDigitsToCollect: {},
    maxDigitsToCollect: {},
    timeoutMessage: { type: NumericCodeCustomerMessageFK },
    errorMessage: { type: NumericCodeCustomerMessageFK },
    timeoutRetries: { type: NumberField(Required, RangeInc(0, 9)) },
    errorRetries: { type: NumberField(Required, RangeInc(0, 9)) }
  },
  { onlyGiven: true }
);

export const NumericCodeFormInitialValues = {
  minDigitsToCollect: 0,
  maxDigitsToCollect: 0,
  timeoutRetries: 0,
  errorRetries: 0
};

export const StandardIvrInitialValues = {
  repeatMessage: false,
  maxRepetitions: 1,
  timeoutRetries: 0,
  errorRetries: 0
};

export const RegistrationCounterInitialValues = {
  counterValue: 0
};

mainStore.treeBlock = new TreeBlockDataSource({
  parent: mainStore.tree.oneDataSource,
  api: api,
  name: "treeBlock",
  schema: AbstractTreeBlockSchema,
  url: "/blocks",
  commons: mainStore.commons
});

export const treeBlockContext = createContext(mainStore.treeBlock);

export type TreeBlockConfigurations = {
  [k in TreeBlockType]: TreeBlockConfig;
};

interface FormCustomAction {
  onClick: (treeBlock: any) => any;
  buttonText: () => string;
}

const resetCounter = async (treeBlock: any): Promise<void> => {
  treeBlock.counterValue = 0;
  treeBlock.restartCounter = true;
  await mainStore.treeBlock.updateTreeBlock(treeBlock, treeBlock.id);
};

export interface TreeBlockConfig {
  // config is shown directly on the block all the time e.g. message name for Message block
  configMapper?: (block: BlockObject) => string;
  // type Function for translation purpose
  name: Function;
  color?: string;
  formSchema: Schema;
  formInitialValues?: {};
  formCustomAction?: FormCustomAction;
  // outputFormSchema is optional and used for dynamic output blocks
  outputFormSchema?: Schema;
  outputCtxValidator?: (
    output: LocalOutput,
    outputOptions: LocalOutput[]
  ) => boolean;
  // outputOptions is optional and used for hardcoded outputs
  outputOptions?: LocalOutput[];
  // output types is used to define allowed output type(s) for given block other than default
  outputTypes?: OutputType[];
  // defines if block is allowed by Service Profile
  if: TreeBlockServiceProfile;
}

const defaultOutputOption = {
  label: t("common:default"),
  value: "DEFAULT",
  type: OutputType.DEFAULT,
  isAddedByDefault: true,
  optionId: "DEFAULT"
};

const extAppBranchOutputs = [
  {
    label: t("common:Error"),
    value: "ERROR",
    type: OutputType.SIMPLE,
    isAddedByDefault: true,
    optionId: "ERROR"
  },
  {
    label: t("hns:tree:treeBlock:output:standardIvr:timeout"),
    value: "TIMEOUT",
    type: OutputType.SIMPLE,
    isAddedByDefault: true,
    optionId: "TIMEOUT"
  }
];

const EnRouteDestinationOutputOptions = [
  {
    label: t("hns:tree:treeBlock:output:enRouteDestination:busy"),
    value: "BUSY",
    type: OutputType.SIMPLE,
    optionId: "BUSY"
  },
  {
    label: t("hns:tree:treeBlock:output:enRouteDestination:noAnswer"),
    value: "NO_ANSWER",
    type: OutputType.SIMPLE,
    optionId: "NO_ANSWER"
  },
  {
    label: t("hns:tree:treeBlock:output:enRouteDestination:limitReached"),
    value: "LIMIT_REACHED",
    type: OutputType.SIMPLE,
    optionId: "LIMIT_REACHED"
  },
  {
    label: t("hns:tree:treeBlock:output:enRouteDestination:congestion"),
    value: "CONGESTION",
    type: OutputType.SIMPLE,
    optionId: "CONGESTION"
  },
  defaultOutputOption
];

const prepareStandardIVRDigitOutputs = () => {
  return DigitsEnum.map(item => {
    return {
      label: item.label,
      value: item.value,
      type: OutputType.SIMPLE,
      optionId: item.value
    };
  });
};

const StandardIvrOutputOptions = [
  defaultOutputOption,
  {
    label: t("hns:tree:treeBlock:output:standardIvr:timeout"),
    value: "TIMEOUT",
    type: OutputType.SIMPLE,
    optionId: "TIMEOUT"
  },
  ...prepareStandardIVRDigitOutputs()
];

const DayOfWeekOutputOptions = [
  {
    label: t("hns:tree:treeBlock:output:dayOfWeek:monday"),
    value: "MONDAY",
    type: OutputType.SIMPLE,
    optionId: "MONDAY"
  },
  {
    label: t("hns:tree:treeBlock:output:dayOfWeek:tuesday"),
    value: "TUESDAY",
    type: OutputType.SIMPLE,
    optionId: "TUESDAY"
  },
  {
    label: t("hns:tree:treeBlock:output:dayOfWeek:wednesday"),
    value: "WEDNESDAY",
    type: OutputType.SIMPLE,
    optionId: "WEDNESDAY"
  },
  {
    label: t("hns:tree:treeBlock:output:dayOfWeek:thursday"),
    value: "THURSDAY",
    type: OutputType.SIMPLE,
    optionId: "THURSDAY"
  },
  {
    label: t("hns:tree:treeBlock:output:dayOfWeek:friday"),
    value: "FRIDAY",
    type: OutputType.SIMPLE,
    optionId: "FRIDAY"
  },
  {
    label: t("hns:tree:treeBlock:output:dayOfWeek:saturday"),
    value: "SATURDAY",
    type: OutputType.SIMPLE,
    optionId: "SATURDAY"
  },
  {
    label: t("hns:tree:treeBlock:output:dayOfWeek:sunday"),
    value: "SUNDAY",
    type: OutputType.SIMPLE,
    optionId: "SUNDAY"
  },
  defaultOutputOption
];

const PresentationOutputOptions = [
  defaultOutputOption,
  {
    label: t("hns:tree:treeBlock:output:clipPresentation:match"),
    value: "MATCHED",
    type: OutputType.MATCHED,
    isAddedByDefault: true,
    optionId: "MATCHED"
  }
];

export function StandardIVRDialOptions(): Type {
  return EnumField(DigitsEnum, Required);
}

const baseSchemaOptions = {
  primaryKey: [],
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  }
};

const CalendarDayTypeTreeBlockOutputSchema = (
  san: RemoteDataSource<any, any>
): Schema =>
  new Schema({
    ...baseSchemaOptions,
    fields: [
      {
        name: "output",
        type: CalendarDayTypesFK,
        title: t("hns:tree:treeBlock:output:calendarDayType"),
        readonly: () => !sanHasCalendar({ data: san.oneDataSource.one })
      }
    ]
  });

const TimeOfDayTreeBlockOutputSchema = new Schema({
  ...baseSchemaOptions,
  fields: [
    {
      name: "output",
      type: () =>
        TextField(
          Pattern(
            TIME_RANGE_PATTERN,
            "",
            t("hns:tree:treeBlock:output:timeOfDay:invalidFormat")()
          )
        ),
      title: t("hns:tree:treeBlock:output:output")
    }
  ]
});

const ExtAppBranchTreeBlockOutputSchema = new Schema({
  ...baseSchemaOptions,
  fields: [
    {
      name: "output",
      type: TextField(Required),
      title: t("hns:tree:treeBlock:output:output")
    }
  ]
});

const NetworkOperatorTreeBlockOutputSchema = new Schema({
  ...baseSchemaOptions,
  fields: [
    {
      name: "output",
      type: NetworkOperatorFK,
      title: t("hns:tree:treeBlock:networkOperator")
    }
  ]
});

const TrafficDistributionOutputSchema = new Schema({
  ...baseSchemaOptions,
  fields: [
    {
      name: "output",
      type: NumberField(Required, RangeInc(1, 99)),
      title: t(
        "hns:tree:treeBlock:output:trafficDistribution:trafficDistributionRate"
      )
    }
  ]
});

const GeographicGroupOutputSchema = new Schema({
  ...baseSchemaOptions,
  fields: [
    {
      name: "output",
      type: AreaSplittingGroupForTreeFK,
      title: t("hns:area:domain")
    }
  ]
});

// DO NOT move this factory to separate module as it affects import order and crashes SAN store initialization
const AdvancedConditionValidatorFactory = (): Validator => {
  const conditionParam =
    mainStore.treeBlock.oneDataSource?.one?.advancedConditionParam;

  if (conditionParam === AdvancedConditionParamValue.INCOMING_XRN) {
    return Pattern(
      ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_XRN,
      "",
      t("hns:tree:treeBlock:output:advancedConditions:invalidFormatXrn")()
    );
  } else if (
    conditionParam === AdvancedConditionParamValue.LAST_SIP_RESPONSE_CODE
  ) {
    return Pattern(
      ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_LAST_SIP_RESPONSE_CODE,
      "",
      t(
        "hns:tree:treeBlock:output:advancedConditions:invalidFormatLastSipResponse"
      )()
    );
  } else if (conditionParam === AdvancedConditionParamValue.SAN_EXTENSION) {
    return Pattern(
      ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_SAN_EXTENSION,
      "",
      t(
        "hns:tree:treeBlock:output:advancedConditions:invalidFormatSanExtension"
      )()
    );
  } else {
    return Pattern(
      ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_NUMBER,
      "",
      t("hns:tree:treeBlock:output:advancedConditions:invalidFormatNumber")()
    );
  }
};

const AdvancedConditionsOutputSchema = new Schema({
  ...baseSchemaOptions,
  fields: [
    {
      name: "output",
      type: () => TextField(Required, AdvancedConditionValidatorFactory()),
      title: t("hns:tree:treeBlock:output:output"),
      if: ctx =>
        isOutputFormOfType(OutputType.SIMPLE, ctx.data.type) ||
        outputHasType(ctx.data.type, OutputType.SIMPLE)
    },
    {
      name: "output",
      type: ListFKCustom,
      title: t("hns:list:domain"),
      if: ctx =>
        isOutputFormOfType(OutputType.LIST, ctx.data.type) ||
        outputHasType(ctx.data.type, OutputType.LIST)
    }
  ]
});

const NumericCodeTreeBlockOutputSchema = new Schema({
  ...baseSchemaOptions,
  fields: [
    {
      name: "output",
      type: ListFKCustom,
      title: t("hns:list:domain"),
      if: ctx =>
        isOutputFormOfType(OutputType.LIST, ctx.data.type) ||
        outputHasType(ctx.data.type, OutputType.LIST)
    },
    {
      name: "output",
      type: () =>
        TextField(
          Required,
          Pattern(
            NUMERIC_RANGE_PATTERN,
            "",
            t("hns:tree:treeBlock:output:numericCode:invalidFormat")()
          ),
          NumericRangeFromLessIncTo,
          NumericRangeFromSameLengthAsTo
        ),
      title: t("hns:tree:treeBlock:output:range"),
      if: ctx =>
        isOutputFormOfType(OutputType.RANGE, ctx.data.type) ||
        outputHasType(ctx.data.type, OutputType.SIMPLE)
    }
  ]
});

const fkDtoConfigMapper = (block: BlockObject): string => block.fkDto?.name;

export const treeBlockConfigurations: TreeBlockConfigurations = {
  destination: {
    configMapper: fkDtoConfigMapper,
    formSchema: DestinationTreeBlockSchema,
    name: t("hns:tree:treeBlock:destination"),
    color: red.A100,
    if: TreeBlockServiceProfile.T_BLOCK_DESTINATION
  },
  enRouteDestination: {
    configMapper: fkDtoConfigMapper,
    formSchema: EnRouteDestinationTreeBlockSchema,
    outputOptions: EnRouteDestinationOutputOptions,
    name: t("hns:tree:treeBlock:enRouteDestination"),
    color: pink.A100,
    if: TreeBlockServiceProfile.T_BLOCK_ER_DESTINATION
  },
  message: {
    configMapper: fkDtoConfigMapper,
    formSchema: MessageTreeBlockSchema,
    name: t("hns:tree:treeBlock:message"),
    color: purple.A100,
    if: TreeBlockServiceProfile.T_BLOCK_MESSAGE
  },
  enRouteMessage: {
    configMapper: fkDtoConfigMapper,
    name: t("hns:tree:treeBlock:enRouteMessage"),
    formSchema: EnRouteMessageTreeBlockSchema,
    outputOptions: [defaultOutputOption],
    color: deepPurple.A100,
    if: TreeBlockServiceProfile.T_BLOCK_ER_MESSAGE
  },
  huntingGroup: {
    configMapper: fkDtoConfigMapper,
    name: t("hns:tree:treeBlock:huntingGroup"),
    formSchema: HuntingGroupTreeBlockSchema,
    color: indigo.A100,
    if: TreeBlockServiceProfile.T_BLOCK_HUNTING_GROUP
  },
  releaseCall: {
    configMapper: (block: BlockObject): string => block.releaseReason || "",
    name: t("hns:tree:treeBlock:releaseCall"),
    formSchema: ReleaseCallTreeBlockSchema,
    color: blue.A100,
    if: TreeBlockServiceProfile.T_BLOCK_RELEASE
  },
  releaseCallPro: {
    configMapper: (block: BlockObject): string => block.releaseReason || "",
    name: t("hns:tree:treeBlock:releaseCallPro"),
    formSchema: ReleaseCallPROTreeBlockSchema,
    color: blue.A200,
    if: TreeBlockServiceProfile.T_BLOCK_RELEASE_PRO
  },
  subtree: {
    name: t("hns:tree:treeBlock:subtree"),
    formSchema: SubTreeTreeBlockSchema,
    color: lightBlue.A100,
    if: TreeBlockServiceProfile.T_BLOCK_SUB_TREE
  },
  ivrMenu: {
    name: t("hns:tree:treeBlock:ivrMenu"),
    formSchema: IVRMenuTreeBlockSchema,
    color: cyan.A100,
    if: TreeBlockServiceProfile.T_BLOCK_IVR_MENU
  },
  dayOfWeek: {
    name: t("hns:tree:treeBlock:dayOfWeek"),
    formSchema: NoParametersTreeBlockSchema,
    outputOptions: DayOfWeekOutputOptions,
    color: teal.A100,
    if: TreeBlockServiceProfile.T_BLOCK_DAY_OF_WEEK
  },
  calendarDayType: {
    name: t("hns:tree:treeBlock:calendarDayType"),
    formSchema: NoParametersTreeBlockSchema,
    outputOptions: [defaultOutputOption],
    outputFormSchema: CalendarDayTypeTreeBlockOutputSchema(mainStore.SAN),
    outputTypes: [OutputType.CALENDAR_DAY_TYPE],
    color: green.A100,
    if: TreeBlockServiceProfile.T_BLOCK_CALENDAR_DAY_TYPE
  },
  timeOfDay: {
    name: t("hns:tree:treeBlock:timeOfDay"),
    formSchema: NoParametersTreeBlockSchema,
    outputOptions: [defaultOutputOption],
    outputFormSchema: TimeOfDayTreeBlockOutputSchema,
    outputCtxValidator: TimeOfDayValidator,
    outputTypes: [OutputType.TIMESLOT],
    color: lightGreen.A100,
    if: TreeBlockServiceProfile.T_BLOCK_TIME_OF_DAY
  },
  standardIvr: {
    name: t("hns:tree:treeBlock:standardIvr"),
    formSchema: StandardIVRTreeBlockSchema,
    formInitialValues: StandardIvrInitialValues,
    outputTypes: [OutputType.SIMPLE],
    outputOptions: StandardIvrOutputOptions,
    color: lime.A100,
    if: TreeBlockServiceProfile.T_BLOCK_STANDARD_IVR
  },
  numericCode: {
    name: t("hns:tree:treeBlock:numericCode"),
    formSchema: NumericCodeTreeBlockSchema,
    formInitialValues: NumericCodeFormInitialValues,
    outputOptions: [defaultOutputOption],
    outputTypes: [OutputType.LIST, OutputType.RANGE],
    outputFormSchema: NumericCodeTreeBlockOutputSchema,
    outputCtxValidator: NumericRangeValidator,
    color: yellow.A100,
    if: TreeBlockServiceProfile.T_BLOCK_NUMERIC_CODE
  },
  geographicGroup: {
    name: t("hns:tree:treeBlock:geographicGroup"),
    formSchema: NoParametersTreeBlockSchema,
    outputOptions: [defaultOutputOption],
    outputFormSchema: GeographicGroupOutputSchema,
    outputTypes: [OutputType.AREA_GROUP],
    color: amber.A100,
    if: TreeBlockServiceProfile.T_BLOCK_GEOGRAPHIC_GROUP
  },
  trafficDistribution: {
    name: t("hns:tree:treeBlock:trafficDistribution"),
    formSchema: NoParametersTreeBlockSchema,
    outputOptions: [defaultOutputOption],
    outputFormSchema: TrafficDistributionOutputSchema,
    outputCtxValidator: TrafficDistributionValidator,
    outputTypes: [OutputType.SIMPLE],
    color: orange.A100,
    if: TreeBlockServiceProfile.T_BLOCK_TRAFFIC
  },
  registrationCounter: {
    configMapper: (block: BlockObject): string =>
      `${block.counterName} (${block.counterValue || 0})`,
    name: t("hns:tree:treeBlock:registrationCounter"),
    formSchema: RegistrationCounterTreeBlockSchema,
    formInitialValues: RegistrationCounterInitialValues,
    formCustomAction: {
      buttonText: t("hns:tree:treeBlock:registrationCounterCustomAction"),
      onClick: resetCounter
    },
    outputOptions: [defaultOutputOption],
    color: red[100],
    if: TreeBlockServiceProfile.T_BLOCK_REGISTRATION_COUNTER
  },
  presentation: {
    name: t("hns:tree:treeBlock:clipPresentation"),
    formSchema: NoParametersTreeBlockSchema,
    outputOptions: PresentationOutputOptions,
    outputTypes: [OutputType.MATCHED],
    color: pink[100],
    if: TreeBlockServiceProfile.T_BLOCK_PRESENTATION
  },
  advancedConditions: {
    name: t("hns:tree:treeBlock:advancedConditions"),
    formSchema: AdvancedConditionsTreeBlockSchema,
    outputOptions: [defaultOutputOption],
    outputFormSchema: AdvancedConditionsOutputSchema,
    outputTypes: [OutputType.SIMPLE, OutputType.LIST],
    color: purple[100],
    if: TreeBlockServiceProfile.T_BLOCK_ADVANCED_CONDITIONS
  },
  extAppDestination: {
    name: t("hns:tree:treeBlock:extAppDestination"),
    formSchema: ExtAppDestination,
    color: deepPurple[100],
    if: TreeBlockServiceProfile.T_BLOCK_EXT_APP_DESTINATION
  },
  extAppBranch: {
    name: t("hns:tree:treeBlock:extAppBranch"),
    formSchema: ExtAppDestination,
    outputOptions: [...extAppBranchOutputs, defaultOutputOption],
    outputFormSchema: ExtAppBranchTreeBlockOutputSchema,
    outputTypes: [OutputType.SIMPLE],
    color: indigo[100],
    if: TreeBlockServiceProfile.T_BLOCK_EXT_APP_BRANCH
  },
  inputDestination: {
    name: t("hns:tree:treeBlock:inputDestination"),
    formSchema: NoParametersTreeBlockSchema,
    color: blue[100],
    if: TreeBlockServiceProfile.T_BLOCK_INPUT_DESTINATION
  },
  networkOperator: {
    name: t("hns:tree:treeBlock:networkOperator"),
    formSchema: NoParametersTreeBlockSchema,
    outputOptions: [defaultOutputOption],
    outputFormSchema: NetworkOperatorTreeBlockOutputSchema,
    outputTypes: [OutputType.NETWORK_OPERATOR],
    color: lightBlue[100],
    if: TreeBlockServiceProfile.T_BLOCK_NETWORK_OPERATOR
  },
  ROOT: {
    name: t("hns:tree:treeBlock:root"),
    formSchema: NoParametersTreeBlockSchema,
    color: brown[100],
    if: TreeBlockServiceProfile.ROOT
  }
};

mainStore.treeBlockOutputs = new TreeBlockOutputsDataSource(
  mainStore.treeBlock,
  treeBlockConfigurations
);

export const treeBlockOutputsContext = createContext(
  mainStore.treeBlockOutputs
);
